import type {
  ChangeFormField,
  ChangeFormFieldType,
  ChangePeriodMode,
} from '../../types/changes';
import type { ChangeFormFieldBuilderApi } from './changeFormFieldBuilderApi';

export interface ReviewQuestionDraft {
  key: string;
  label: string;
  field_type: ChangeFormFieldType;
  is_required: boolean;
  /** Opções de lista ou textos Sim/Não (uma por linha). */
  options: string;
  help_text?: string | null;
  col_span?: 6 | 12;
  section_name?: string | null;
  period_mode?: ChangePeriodMode | null;
  weekday_constraint?: number | null;
  date_revision_limit?: number | null;
  conditional_field_id?: number | null;
  conditional_operator?: string | null;
  conditional_value?: string | null;
  default_value?: string | null;
  select_search_enabled?: boolean;
}

export const parseQuestionOptions = (raw: string) =>
  raw
    .split(/\r?\n|;/)
    .map((item) => item.trim())
    .filter(Boolean);

export const INSTANCE_CHECKLIST_FIELD_TYPES = new Set<ChangeFormFieldType>([
  'text_short',
  'text_long',
  'integer',
  'decimal',
  'date',
  'datetime',
  'period',
  'period_datetime',
  'list',
  'yes_no',
]);

function stableNegativeId(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  if (hash === 0) hash = -1;
  return hash > 0 ? -hash : hash;
}

function fieldFromPayload(
  id: number,
  payload: Record<string, unknown>,
  sortOrder: number,
  previous?: ChangeFormField,
): ChangeFormField {
  const fieldType = payload.field_type as ChangeFormFieldType;
  return {
    id,
    kind: 'extra',
    native_key: null,
    label: String(payload.label || ''),
    field_type: fieldType,
    period_mode: (payload.period_mode as ChangeFormField['period_mode']) ?? previous?.period_mode ?? null,
    weekday_constraint:
      payload.weekday_constraint !== undefined
        ? (payload.weekday_constraint as number | null)
        : (previous?.weekday_constraint ?? null),
    date_revision_limit:
      payload.date_revision_limit !== undefined
        ? (payload.date_revision_limit as number | null)
        : (previous?.date_revision_limit ?? null),
    is_required: Boolean(payload.is_required),
    help_text: (payload.help_text as string | null) ?? null,
    options: Array.isArray(payload.options) ? (payload.options as string[]) : [],
    sort_order: sortOrder,
    col_span: payload.col_span === 6 ? 6 : 12,
    section_name: (payload.section_name as string | null) ?? null,
    is_visible: payload.is_visible !== undefined ? Boolean(payload.is_visible) : true,
    is_locked: false,
    skips_auto_checklist: Boolean(payload.skips_auto_checklist),
    select_search_enabled: Boolean(payload.select_search_enabled),
    conditional_field_id: (payload.conditional_field_id as number | null) ?? null,
    conditional_operator: (payload.conditional_operator as string | null) ?? null,
    conditional_value: (payload.conditional_value as string | null) ?? null,
    default_value: (payload.default_value as string | null) ?? null,
  };
}

export function questionsToFormFields(questions: ReviewQuestionDraft[]): ChangeFormField[] {
  return (questions || [])
    .filter((item) => INSTANCE_CHECKLIST_FIELD_TYPES.has(item.field_type))
    .map((item, index) => {
      const optionsText = item.options || '';
      const options =
        item.field_type === 'list' || item.field_type === 'yes_no'
          ? parseQuestionOptions(optionsText)
          : [];
      return {
        id: stableNegativeId(item.key),
        kind: 'extra' as const,
        native_key: null,
        label: item.label,
        field_type: item.field_type,
        period_mode: item.period_mode ?? null,
        weekday_constraint: item.weekday_constraint ?? null,
        date_revision_limit: item.date_revision_limit ?? null,
        is_required: item.is_required,
        help_text: item.help_text ?? null,
        options,
        sort_order: (index + 1) * 10,
        col_span: item.col_span === 6 ? 6 : 12,
        section_name: item.section_name ?? null,
        is_visible: true,
        is_locked: false,
        conditional_field_id: item.conditional_field_id ?? null,
        conditional_operator: item.conditional_operator ?? null,
        conditional_value: item.conditional_value ?? null,
        default_value: item.default_value ?? null,
        select_search_enabled: Boolean(item.select_search_enabled),
      };
    });
}

export function formFieldsToReviewQuestions(fields: ChangeFormField[]): ReviewQuestionDraft[] {
  return [...fields]
    .filter((item) => item.kind === 'extra' && INSTANCE_CHECKLIST_FIELD_TYPES.has(item.field_type))
    .sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0))
    .map((item) => ({
      key: `field-${item.id}`,
      label: item.label,
      field_type: item.field_type,
      is_required: item.is_required,
      help_text: item.help_text ?? undefined,
      options:
        item.field_type === 'list' || item.field_type === 'yes_no'
          ? (item.options || []).join('\n')
          : '',
      col_span: item.col_span === 6 ? 6 : 12,
      section_name: item.section_name ?? undefined,
      period_mode: item.period_mode ?? undefined,
      weekday_constraint: item.weekday_constraint ?? undefined,
      date_revision_limit: item.date_revision_limit ?? undefined,
      conditional_field_id: item.conditional_field_id ?? undefined,
      conditional_operator: item.conditional_operator ?? undefined,
      conditional_value: item.conditional_value ?? undefined,
      default_value: item.default_value ?? undefined,
      select_search_enabled: item.select_search_enabled ?? undefined,
    }));
}

/** API local para o ChangeFormFieldBuilder (não persiste em grupo/modelo). */
export function createInstanceChecklistFieldApi(
  initialQuestions: ReviewQuestionDraft[],
  onSync: (questions: ReviewQuestionDraft[]) => void,
): ChangeFormFieldBuilderApi {
  let items = questionsToFormFields(initialQuestions);
  let nextSyntheticId =
    items.reduce((min, item) => (item.id < min ? item.id : min), -1) - 1;

  const sync = () => {
    onSync(formFieldsToReviewQuestions(items));
  };

  return {
    load: async () => ({ items: [...items] }),
    createField: async (payload) => {
      const id = nextSyntheticId;
      nextSyntheticId -= 1;
      const sortOrder = items.length ? Math.max(...items.map((item) => item.sort_order || 0)) + 10 : 10;
      const created = fieldFromPayload(id, payload, sortOrder);
      items = [...items, created];
      sync();
      return created;
    },
    updateField: async (id, payload) => {
      items = items.map((field) =>
        field.id === id ? fieldFromPayload(id, payload, field.sort_order || 0, field) : field,
      );
      sync();
      return items.find((field) => field.id === id);
    },
    deleteField: async (id) => {
      items = items
        .filter((field) => field.id !== id)
        .map((field) =>
          field.conditional_field_id === id
            ? {
                ...field,
                conditional_field_id: null,
                conditional_operator: null,
                conditional_value: null,
              }
            : field,
        );
      sync();
    },
    reorderFields: async (ids) => {
      const byId = new Map(items.map((item) => [item.id, item]));
      items = ids
        .map((id, index) => {
          const field = byId.get(id);
          if (!field) return null;
          return { ...field, sort_order: (index + 1) * 10 };
        })
        .filter(Boolean) as ChangeFormField[];
      sync();
      return { items: [...items] };
    },
  };
}

export function templateFieldsToReviewQuestions(
  templateId: number,
  fields: ChangeFormField[],
): ReviewQuestionDraft[] {
  const filtered = fields.filter(
    (field) =>
      INSTANCE_CHECKLIST_FIELD_TYPES.has(field.field_type) && !field.is_system_preset,
  );
  const instanceIdByTemplateId = new Map<number, number>();
  filtered.forEach((field, index) => {
    const key = `tpl-${templateId}-${field.id}-${index}`;
    instanceIdByTemplateId.set(field.id, stableNegativeId(key));
  });
  return filtered.map((field, index) => {
    const key = `tpl-${templateId}-${field.id}-${index}`;
    const mappedConditional =
      field.conditional_field_id && instanceIdByTemplateId.has(field.conditional_field_id)
        ? instanceIdByTemplateId.get(field.conditional_field_id)
        : undefined;
    return {
      key,
      label: field.label,
      field_type: field.field_type,
      is_required: field.is_required,
      help_text: field.help_text ?? undefined,
      options:
        field.field_type === 'list' || field.field_type === 'yes_no'
          ? (field.options || []).join('\n')
          : '',
      col_span: field.col_span === 6 ? 6 : 12,
      section_name: field.section_name ?? undefined,
      period_mode: field.period_mode ?? undefined,
      weekday_constraint: field.weekday_constraint ?? undefined,
      date_revision_limit: field.date_revision_limit ?? undefined,
      conditional_field_id: mappedConditional ?? undefined,
      conditional_operator: field.conditional_operator ?? undefined,
      conditional_value: field.conditional_value ?? undefined,
      default_value: field.default_value ?? undefined,
      select_search_enabled: field.select_search_enabled ?? undefined,
    };
  });
}
